import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { analytics } from '../services/analytics';
import SEO from '../components/SEO';

// Icons
const CheckIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ZapIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const CodeIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

const DesktopIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="14" height="8" x="2" y="3" rx="2" ry="2"/>
    <path d="m9 21 3-9 3 9"/>
    <path d="M12 21h8"/>
  </svg>
);

const XIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const WebDeveloperQuotingSoftware = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  useEffect(() => {
    analytics.track('Web Developer Landing Page View');
  }, []);

  useEffect(() => {
    const controlHeader = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        // Scrolling up or near top - show header
        setIsVisible(true);
      } else {
        // Scrolling down - hide header
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', controlHeader);
    return () => window.removeEventListener('scroll', controlHeader);
  }, [lastScrollY]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Web Developer Quote Software - Interactive Proposals That Convert | QuoteTier"
        description="Web developer quote software that lets clients choose their own upgrades. Turn $2,500 basic websites into $7,500+ enterprise solutions with interactive proposals."
        canonical="/web-developer-quote-software"
        ogTitle="Web Developer Quote Software That Increases Deal Size"
        ogDescription="Interactive web dev proposals that let clients upsell themselves. Transform flat quotes into engaging, mobile-optimized experiences."
        noindex={true}
      />
      {/* Header */}
      <header 
        className={`fixed top-4 left-1/2 z-50 w-[90%] max-w-4xl -translate-x-1/2 rounded-2xl border border-black/10 bg-white/95 shadow-lg transition-all duration-300 ease-in-out ${
          isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}
      >
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center">
            <ZapIcon className="h-4 w-4 mr-2 text-primary" />
            <span className="font-instrument text-xl md:text-2xl">QuoteTier</span>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 text-sm font-medium">
            <Link to="/blog/" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'navigation', action: 'signup' })}>
              <Button className="font-instrument text-xl px-4 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Start Free
              </Button>
            </Link>
          </nav>
          
          {/* Mobile Navigation */}
          <div className="md:hidden flex items-center space-x-2">
            <Link to="/blog/" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'mobile-navigation', action: 'signup' })}>
              <Button className="font-instrument text-sm px-3 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-32 md:py-40 lg:py-48">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl tracking-tight leading-tight mb-6">
                Web developer quotes that<br />
                <span className="text-primary font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl">sell premium features.</span>
              </h1>
              <p className="font-jetbrains mt-6 md:mt-8 text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed mb-8">
                Stop losing money on basic website quotes. QuoteTier's interactive proposals let clients choose e-commerce, integrations, and premium features—turning $2,500 sites into $7,500+ solutions automatically.
              </p>
              <div className="font-instrument mt-12 flex flex-col sm:flex-row gap-6 justify-center items-center mb-6">
                <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'webdev-hero', action: 'signup' })}>
                  <Button className="px-8 py-4 text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                    Start Free — 3 Quotes/Mo
                  </Button>
                </Link>
                <Link to="/demo-quote/web-design" onClick={() => analytics.track('Demo Clicked', { location: 'webdev-hero', industry: 'web-design' })}>
                  <Button className="px-8 py-4 text-xl font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg" variant="outline">
                    See Web Dev Quote Demo →
                  </Button>
                </Link>
              </div>
              <p className="font-jetbrains mt-10 text-lg text-gray-500">
                No credit card required • Setup in 5 minutes • Perfect for agencies & freelancers
              </p>
            </div>
          </div>
        </section>

        {/* Web Development Quote Example */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">
                See How Clients Choose Higher-Value Website Packages
              </h2>
              <p className="font-jetbrains text-lg text-gray-600">
                Instead of one flat price, clients see three clear options and naturally upgrade to premium features
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3 max-w-5xl mx-auto">
              {/* Basic Package */}
              <Card className="relative border border-black transition-all">
                <CardHeader>
                  <div className="flex justify-between items-center mb-2">
                    <CardTitle className="font-instrument text-xl">Starter Website</CardTitle>
                    <span className="text-2xl font-bold">$2,500</span>
                  </div>
                  <CardDescription className="font-jetbrains">Professional 5-page business website</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />5-page custom design</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Mobile responsive</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Contact form</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Basic SEO setup</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />3 months support</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Popular Package */}
              <Card className="relative border border-black transform scale-105">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-black text-white px-4 py-1 rounded-full text-sm font-semibold">Most Popular</span>
                </div>
                <CardHeader>
                  <div className="flex justify-between items-center mb-2">
                    <CardTitle className="font-instrument text-xl">Business Pro Website</CardTitle>
                    <span className="text-2xl font-bold">$4,500</span>
                  </div>
                  <CardDescription className="font-jetbrains">Advanced website with enhanced features</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />10-page custom design</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Mobile & tablet optimized</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Contact forms & lead capture</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Advanced SEO optimization</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Social media integration</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Analytics setup</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />6 months support & updates</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Premium Package */}
              <Card className="relative border border-black">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-black text-white px-4 py-1 rounded-full text-sm font-semibold">Best Value</span>
                </div>
                <CardHeader>
                  <div className="flex justify-between items-center mb-2">
                    <CardTitle className="font-instrument text-xl">Enterprise Website Solution</CardTitle>
                    <span className="text-2xl font-bold">$7,500</span>
                  </div>
                  <CardDescription className="font-jetbrains">Complete digital presence with e-commerce</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Unlimited pages</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Custom e-commerce functionality</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Advanced lead generation</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Comprehensive SEO strategy</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Social media automation</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Performance optimization</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Security features</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />12 months support & maintenance</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Monthly performance reports</li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <div className="text-center mt-12">
              <Link to="/demo-quote/web-design" onClick={() => analytics.track('Demo Clicked', { location: 'webdev-pricing', industry: 'web-design' })}>
                <Button className="px-8 py-4 text-lg font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                  Try This Interactive Web Dev Quote →
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Why Web Developers Choose QuoteTier */}
        <section className="py-16 md:py-24">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">
                Why Web Developers & Agencies Choose QuoteTier
              </h2>
              <p className="font-jetbrains text-lg text-gray-600">
                The only quoting software designed for developers who want to sell premium features, not compete on price
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              <Card className="border border-black">
                <CardHeader>
                  <div className="border border-black bg-primary/10 text-primary h-12 w-12 rounded-lg flex items-center justify-center mb-4">
                    <CodeIcon className="h-6 w-6" />
                  </div>
                  <CardTitle className="font-instrument text-xl">Pre-Built Web Dev Packages</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground font-jetbrains text-sm">Ready-to-use Good/Better/Best website packages with all the premium features clients want - from basic sites to full e-commerce solutions.</p>
                </CardContent>
              </Card>

              <Card className="border border-black">
                <CardHeader>
                  <div className="border border-black bg-primary/10 text-primary h-12 w-12 rounded-lg flex items-center justify-center mb-4">
                    <ZapIcon className="h-6 w-6" />
                  </div>
                  <CardTitle className="font-instrument text-xl">Real-Time Proposal Tracking</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground font-jetbrains text-sm">Know exactly when clients open your proposal, how long they spend reviewing it, and which features they're most interested in.</p>
                </CardContent>
              </Card>

              <Card className="border border-black">
                <CardHeader>
                  <div className="border border-black bg-primary/10 text-primary h-12 w-12 rounded-lg flex items-center justify-center mb-4">
                    <DesktopIcon className="h-6 w-6" />
                  </div>
                  <CardTitle className="font-instrument text-xl">Beautiful, Professional Design</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground font-jetbrains text-sm">Impress clients with gorgeous, interactive proposals that reflect your design skills and attention to detail.</p>
                </CardContent>
              </Card>
            </div>

            <div className="mt-12 bg-white border border-black rounded-2xl p-8 text-center">
              <h3 className="font-instrument text-2xl font-bold mb-4">Perfect for Freelancers & Development Agencies</h3>
              <p className="font-jetbrains text-gray-700 mb-6 max-w-3xl mx-auto">
                Whether you're a solo freelancer or running a development agency, QuoteTier helps you present your services professionally and automatically upsell clients to premium features. Stop competing on price - let your value sell itself.
              </p>
              <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'webdev-perfect-for', action: 'signup' })}>
                <Button className="px-8 py-3 text-lg font-semibold bg-black text-white hover:bg-gray-800 rounded-lg">
                  Start Free
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container max-w-5xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">
                QuoteTier vs Traditional Web Dev Proposals
              </h2>
              <p className="font-jetbrains text-lg text-gray-600">
                See why smart developers are switching from PDF proposals to interactive quotes
              </p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-6 px-6 font-instrument text-lg font-semibold text-gray-900 bg-gray-50">
                      Feature
                    </th>
                    <th className="text-center py-6 px-6 bg-black text-white">
                      <div className="flex flex-col items-center gap-2">
                        <span className="font-instrument text-lg font-bold">QuoteTier</span>
                        <span className="bg-white/20 text-white text-xs font-medium px-3 py-1 rounded-full">
                          Dev Optimized
                        </span>
                      </div>
                    </th>
                    <th className="text-center py-6 px-6 font-instrument text-lg font-semibold text-gray-700 bg-gray-50">
                      PDF Proposals
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-jetbrains font-medium">Client can choose feature level</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/30">
                    <td className="py-4 px-6 font-jetbrains font-medium">Automatic upselling to e-commerce</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-jetbrains font-medium">Track when client views proposal</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/30">
                    <td className="py-4 px-6 font-jetbrains font-medium">Mobile-friendly for clients</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-jetbrains font-medium">Digital contract signing</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="bg-gray-50/30">
                    <td className="py-4 px-6 font-jetbrains font-medium">Time to create proposal</td>
                    <td className="py-4 px-6 text-center">
                      <span className="font-jetbrains font-semibold text-gray-800 bg-gray-100 px-3 py-1 rounded-full text-sm">
                        5 minutes
                      </span>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <span className="font-jetbrains text-gray-600 text-sm">2+ hours</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Success Stories */}
        <section className="py-16 md:py-24">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">
                Web Developers Increase Project Value by 60%+ with QuoteTier
              </h2>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Average Project Increase</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold mb-2">+$2,000</div>
                  <p className="font-jetbrains text-sm text-muted-foreground">Clients naturally choose enhanced features when they can see the clear value difference.</p>
                </CardContent>
              </Card>

              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Proposal Acceptance Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold mb-2">90%</div>
                  <p className="font-jetbrains text-sm text-muted-foreground">Interactive proposals get accepted faster and more often than traditional PDFs.</p>
                </CardContent>
              </Card>

              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Time Saved Per Proposal</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold mb-2">90+ min</div>
                  <p className="font-jetbrains text-sm text-muted-foreground">No more creating custom proposals from scratch - use templates and personalize instantly.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-gray-900 to-black text-white">
          <div className="container max-w-4xl mx-auto px-4 text-center">
            <h2 className="font-instrument text-4xl md:text-5xl font-bold mb-6">
              Ready to Sell More Premium Web Development?
            </h2>
            <p className="font-jetbrains text-xl text-gray-200 mb-8">
              Join hundreds of web developers and agencies using QuoteTier to automatically upsell clients to enterprise-level solutions.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
              <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'webdev-final-cta', action: 'signup' })}>
                <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-white text-black hover:bg-gray-100 rounded-lg shadow-lg">
                  Start Free — 3 Quotes/Mo
                </Button>
              </Link>
              <Link to="/demo-quote/web-design" onClick={() => analytics.track('Demo Clicked', { location: 'webdev-final-cta', industry: 'web-design' })}>
                <Button className="px-8 py-4 text-lg font-semibold border-2 border-white text-white hover:bg-white/10 rounded-lg" variant="outline">
                  Try Interactive Demo First →
                </Button>
              </Link>
            </div>
            
            <p className="font-jetbrains text-sm text-gray-400">
              No credit card required • Cancel anytime • Setup in minutes
            </p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t">
        <div className="container max-w-7xl mx-auto px-4 py-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-muted-foreground">&copy; 2025 QuoteTier. All rights reserved. <span className="footer-credit">Site by <a href="https://novanorthstudio.com/?utm_source=quotetier.com&utm_medium=referral&utm_campaign=site-credit">Nova North Studio</a></span></p>
          <div className="flex gap-4 mt-4 md:mt-0">
            <Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Terms</Link>
            <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy</Link>
            <a href="mailto:info@quotetier.com" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WebDeveloperQuotingSoftware;
